import React, { useState } from 'react';
import { ArrowRight, Shield, Code, Globe, Server, CheckCircle, Lock, Smartphone } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const ApplicationSecurityPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const appSecuritySolutions = [
    {
      icon: Code,
      title: "Secure Code Development",
      description: "Integrate security into the development lifecycle with secure coding practices and automated testing.",
      features: ["Static code analysis", "Dynamic testing", "Dependency scanning", "Security training"]
    },
    {
      icon: Globe,
      title: "Web Application Protection",
      description: "Comprehensive web application security with WAF, DDoS protection, and vulnerability management.",
      features: ["Web application firewall", "DDoS mitigation", "Bot protection", "API security"]
    },
    {
      icon: Smartphone,
      title: "Mobile Application Security",
      description: "End-to-end mobile app security for iOS and Android applications with runtime protection.",
      features: ["Mobile app testing", "Runtime protection", "API security", "Data encryption"]
    },
    {
      icon: Server,
      title: "API Security & Management",
      description: "Secure API development, deployment, and management with comprehensive monitoring and protection.",
      features: ["API gateway", "Authentication", "Rate limiting", "Threat detection"]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 text-slate-100' 
          : 'bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1181263/pexels-photo-1181263.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Application Security"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-violet-950/90' 
              : 'bg-gradient-to-r from-violet-50/90 to-fuchsia-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Code className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-violet-400' : 'text-violet-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Application <span className={isDarkMode ? "text-violet-400" : "text-violet-600"}>Security</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Secure your applications throughout the development lifecycle with 
              comprehensive testing, protection, and monitoring solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-violet-600 hover:bg-violet-700 text-white hover:shadow-violet-500/25" 
                    : "bg-violet-600 hover:bg-violet-700 text-white hover:shadow-violet-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                App Security Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-violet-400 text-violet-400 hover:bg-violet-400 hover:text-slate-900" 
                    : "border-violet-600 text-violet-600 hover:bg-violet-600 hover:text-white"
                }`}
              >
                View Security Pipeline
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Application Security Solutions Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Comprehensive Application Security
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Protect your applications from development to deployment and beyond
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {appSecuritySolutions.map((solution, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <solution.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-violet-400' : 'text-violet-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{solution.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {solution.description}
                </p>
                <ul className="space-y-2">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default ApplicationSecurityPage;